import { ACTIVITY_LOG, ENTITY_COUNTS, TRANSACTION_LOG, USER_UUID } from '../util/Constants';
import { getJsonLocalStore, getLocalStore, setJsonLocalStore } from '../util/Helpers';

const MAX_ENTRIES = 500;
const DEFAULT_DAYS = 30;

const createId = () => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const readEntries = (key) => {
    const entries = getJsonLocalStore(key, []);
    return Array.isArray(entries) ? entries : [];
};

const writeEntries = (key, entries) => {
    setJsonLocalStore(key, entries.slice(-MAX_ENTRIES));
};

const eventDate = (entry) => entry?.eventTime || entry?.time || entry?.createdAt;
const getActorUuid = () => {
    try {
        return getLocalStore(USER_UUID) || null;
    } catch (error) {
        return null;
    }
};
const isWithinDays = (entry, days, now = Date.now()) => {
    const timestamp = Date.parse(eventDate(entry));
    return !Number.isNaN(timestamp) && timestamp >= now - (days * 24 * 60 * 60 * 1000);
};

export function recordActivity({
    type = 'system',
    action = 'System action',
    entityType,
    entityUuid,
    userUuid,
    userName,
    eventTime = new Date().toISOString(),
    metadata
} = {}) {
    const entry = {
        uuid: createId(),
        type,
        action,
        ...(entityType ? { entityType } : {}),
        ...(entityUuid ? { entityUuid } : {}),
        userUuid: userUuid || getActorUuid(),
        ...(userName ? { userName } : {}),
        eventTime,
        time: eventTime,
        createdAt: eventTime,
        ...(metadata ? { metadata } : {})
    };

    writeEntries(ACTIVITY_LOG, [...readEntries(ACTIVITY_LOG), entry]);
    return entry;
}

export function recordTransaction({
    type = 'transaction',
    action,
    description,
    amount = 0,
    entityType,
    entityUuid,
    eventTime = new Date().toISOString(),
    metadata
} = {}) {
    const numericAmount = Number(amount);
    const transaction = {
        uuid: createId(),
        type,
        description: description || action || 'Transaction recorded',
        amount: Number.isFinite(numericAmount) ? numericAmount : 0,
        ...(entityType ? { entityType } : {}),
        ...(entityUuid ? { entityUuid } : {}),
        eventTime,
        createdAt: eventTime,
        ...(metadata ? { metadata } : {})
    };

    writeEntries(TRANSACTION_LOG, [...readEntries(TRANSACTION_LOG), transaction]);
    recordActivity({
        type,
        action: action || transaction.description,
        entityType,
        entityUuid,
        eventTime,
        metadata: { amount: transaction.amount, ...(metadata || {}) }
    });
    return transaction;
}

export function recordEntityChange(entityType, delta) {
    if (entityType !== 'user' && entityType !== 'model') return;

    const counts = getJsonLocalStore(ENTITY_COUNTS, {});
    const currentCount = Number(counts?.[`${entityType}s`]) || 0;
    const nextCount = Math.max(0, currentCount + Number(delta || 0));
    setJsonLocalStore(ENTITY_COUNTS, {
        ...counts,
        [`${entityType}s`]: nextCount
    });
}

export function getActivity(days = DEFAULT_DAYS, limit = 10) {
    return readEntries(ACTIVITY_LOG)
        .filter(entry => isWithinDays(entry, days))
        .sort((left, right) => Date.parse(eventDate(right)) - Date.parse(eventDate(left)))
        .slice(0, limit);
}

export function getActivitySummary(days = DEFAULT_DAYS) {
    const entries = readEntries(ACTIVITY_LOG).filter(entry => isWithinDays(entry, days));
    return { count: entries.length };
}

export function getTransactionSummary(days = DEFAULT_DAYS) {
    const transactions = readEntries(TRANSACTION_LOG).filter(entry => isWithinDays(entry, days));
    return {
        count: transactions.length,
        total: transactions.reduce((sum, transaction) => sum + (Number(transaction.amount) || 0), 0)
    };
}

export function getDashboardMetrics(days = DEFAULT_DAYS) {
    const counts = getJsonLocalStore(ENTITY_COUNTS, {});
    const activity = getActivitySummary(days);
    const transactions = getTransactionSummary(days);

    return {
        stats: {
            totalUsers: Number(counts?.users) || 0,
            totalModels: Number(counts?.models) || 0,
            activityCount30Days: activity.count
        },
        activity30Days: activity,
        transactions30Days: transactions,
        recentActivity: getActivity(days)
    };
}

export function clearActivityData() {
    setJsonLocalStore(ACTIVITY_LOG, []);
    setJsonLocalStore(TRANSACTION_LOG, []);
    setJsonLocalStore(ENTITY_COUNTS, {});
}
